import { RefObject, useEffect, useMemo, useState } from 'react';

import { useClientHasRendered } from '@domain/session/useClientHasRendered';

/**
 * The `useDistanceFromTop` function returns the vertical scroll distance from a scroll element relative to the top of its container.
 * Without the optional parameters it returns the vertical scroll distance of the window relative to the top of the viewport
 * @param containerReference - An optional reference to the container of the scrollable element.
 * @param scrollElementRef - An optional reference to a scrollable element.
 */
export const useDistanceFromTop = (
  containerRef?: RefObject<HTMLElement | null>,
  scrollElementRef?: RefObject<HTMLElement | null>,
): number => {
  const [distanceFromTop, setDistanceFromTop] = useState(0);
  const hasClientRendered = useClientHasRendered();
  const onScroll = useMemo(() => {
    return () => {
      const containerRect = containerRef?.current?.getBoundingClientRect();
      const elementRect = scrollElementRef?.current?.getBoundingClientRect();
      const offsetTop = containerRect ? containerRect.top : 0;
      const scrollTop = elementRect ? elementRect.top : window.scrollY;
      setDistanceFromTop(Math.abs(scrollTop - offsetTop));
    };
  }, [containerRef, scrollElementRef]);

  useEffect(() => {
    if (!hasClientRendered) {
      return;
    }

    const container = containerRef?.current ?? document;
    container.addEventListener('scroll', onScroll);

    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [hasClientRendered, containerRef, onScroll]);

  return distanceFromTop;
};
